import { useState } from "react";
import { Loader2, MoreVertical } from "lucide-react";
import { useJobs, useArchiveJob, type ProcessingJobList } from "@/lib/api";
import { getProjectName } from "@/lib/projectName";
import { getStepLabel, getStepProgress } from "@/lib/processingSteps";
import { ProjectContextMenu, type MenuPosition } from "./ProjectContextMenu";
import { ProjectInfoModal } from "./ProjectInfoModal";
import { RenameProjectDialog } from "./RenameProjectDialog";
import { DeleteProjectDialog } from "./DeleteProjectDialog";

interface AllProjectsViewProps {
  onSelectJob: (position: number) => void;
  filterProcessing?: boolean;
}

function formatDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

const statusBadge = (status: string) => {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-700";
    case "PROCESSING":
    case "PENDING":
      return "bg-amber-100 text-amber-700";
    case "FAILED":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

export function AllProjectsView({ onSelectJob, filterProcessing }: AllProjectsViewProps) {
  const { data: jobs, isLoading } = useJobs();
  const archiveJob = useArchiveJob();
  const [menu, setMenu] = useState<{ job: ProcessingJobList; pos: MenuPosition } | null>(null);
  const [infoJob, setInfoJob] = useState<ProcessingJobList | null>(null);
  const [renameJob, setRenameJob] = useState<ProcessingJobList | null>(null);
  const [deleteJob, setDeleteJob] = useState<ProcessingJobList | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, job: ProcessingJobList) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_W = 208;
    setMenu({
      job,
      pos: {
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - MENU_W),
      },
    });
  };

  const filteredJobs = filterProcessing
    ? jobs?.filter((j) => j.status === "PROCESSING" || j.status === "PENDING")
    : jobs;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const title = filterProcessing ? "Processing" : "All projects";

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b shrink-0">
        <h2 className="text-lg font-medium">{title}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {filteredJobs?.length ?? 0} project{(filteredJobs?.length ?? 0) !== 1 ? "s" : ""}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {!filteredJobs || filteredJobs.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-muted-foreground">
              {filterProcessing ? "No active processing jobs" : "No projects yet"}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredJobs.map((job) => {
              const isClickable = job.status === "COMPLETED";
              return (
                <div
                  key={job.id}
                  role="button"
                  tabIndex={isClickable ? 0 : -1}
                  onClick={() => isClickable && onSelectJob(job.position)}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onSelectJob(job.position);
                    }
                  }}
                  className={`w-full px-6 py-3 flex items-center gap-4 text-left transition-colors group ${
                    isClickable ? "hover:bg-muted/50 cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{getProjectName(job)}</div>
                    {job.status === "PROCESSING" || job.status === "PENDING" ? (
                      <div className="mt-1 space-y-1">
                        <span className="text-xs text-muted-foreground">{getStepLabel(job.step)}</span>
                        <div className="h-[3px] w-full max-w-[120px] rounded-full bg-primary/20 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all duration-500"
                            style={{ width: `${getStepProgress(job.step)}%` }}
                          />
                        </div>
                      </div>
                    ) : job.report_preview?.summary ? (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{job.report_preview.summary}</p>
                    ) : null}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{formatDate(job.created_at)}</span>
                  {job.detected_language && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-muted text-muted-foreground shrink-0 uppercase tracking-wide"
                      title="Detected source language"
                    >
                      {job.detected_language}
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label="Project options"
                    onClick={(e) => openMenu(e, job)}
                    className="opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-muted-foreground/10 rounded p-1 transition-opacity shrink-0"
                  >
                    <MoreVertical className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${statusBadge(job.status)}`}>
                    {job.status === "COMPLETED" ? "Completed" : job.status === "FAILED" ? "Failed" : "Processing"}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {menu && (
        <ProjectContextMenu
          position={menu.pos}
          onClose={() => setMenu(null)}
          onInfo={() => { setInfoJob(menu.job); setMenu(null); }}
          onRename={() => { setRenameJob(menu.job); setMenu(null); }}
          onArchive={() => {
            archiveJob.mutate(menu.job.id);
            setMenu(null);
          }}
          onDelete={() => { setDeleteJob(menu.job); setMenu(null); }}
        />
      )}
      {infoJob && (
        <ProjectInfoModal
          job={infoJob}
          open
          onOpenChange={(o) => { if (!o) setInfoJob(null); }}
        />
      )}
      {renameJob && (
        <RenameProjectDialog
          job={renameJob}
          open
          onOpenChange={(o) => { if (!o) setRenameJob(null); }}
        />
      )}
      {deleteJob && (
        <DeleteProjectDialog
          job={deleteJob}
          open
          onOpenChange={(o) => { if (!o) setDeleteJob(null); }}
          onDeleted={() => setDeleteJob(null)}
        />
      )}
    </div>
  );
}
